import * as React from 'react';
import * as UI from 'core/ui';

import { AddressDTO, AddressLocationDTO, Res } from 'core/api';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import * as Images from 'pos/Images';
import { Result } from 'core/infrastructure';
import { Message, Modal } from 'core/ui-next/modal';

type Props = {
	currentAddress: AddressDTO
} & Modal.Actions<EditDeliveryAdress>;

type Errors = { [field in keyof DeliveryAddress]?: string; };

export type DeliveryAddress = AddressDTO & { FloorAndDoor: string, Number: string };

type State = DeliveryAddress & {
	Errors: Errors;
	Location: AddressLocationDTO | null;
};

const emptyState: State = {
	Street: '',
	City: '',
	Region: '',
	FloorAndDoor: '',
	ZipCode: '',
	Number: '',
	Errors: {},
	Location: null
};

export function validate(address: DeliveryAddress): { isValid: boolean, errors: Errors } {
	const errors: Errors = {};

	if (address.Street.trim() === '')
		errors['Street'] = Res.Msg.MSG_VALUE_REQUIRED();

	if (address.Number.trim() === '')
		errors['Number'] = Res.Msg.MSG_VALUE_REQUIRED();

	if (address.City.trim() === '')
		errors['City'] = Res.Msg.MSG_VALUE_REQUIRED();

	if (address.ZipCode.trim() === '')
		errors['ZipCode'] = Res.Msg.MSG_VALUE_REQUIRED();

	if (address.Region.trim() === '')
		errors['Region'] = Res.Msg.MSG_VALUE_REQUIRED();

	const isValid = Object.keys(errors).every(k => !(errors as any)[k]);

	return { isValid, errors };
}


export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { error?: string, label: string }>(function fn({ className, error, label, ...rest }, ref) {
	return <div className={`form-group col-sm-12 ${error ? 'has-error' : ''}`}>
		<label className='control-label'>{label}</label>
		<input ref={ref} className='form-control' {...rest} />
		<UI.ErrorBlock msg={error} />
	</div>;
});

class ShowEditDeliveryAddressScreen extends PosScreenClassComponent<Props, State> {

	private autoComplete: google.maps.places.Autocomplete | null;
	private autoCompleteRef = React.createRef<HTMLInputElement>();

	constructor(props: Props) {
		super(props);
		this.autoComplete = null;
		this.state = props.currentAddress
			? { ...emptyState, ...props.currentAddress }
			: emptyState;
	}

	protected getScreenId(): string {
		return 'EDIT_DELIVERY_ADDRESS';
	}

	protected getTitle(): string {
		return Res.Web.SHOW_EDIT_DELIVERY_ADDRESS_TITLE();
	}

	protected getImage() {
		return Images.Address;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({ onOK: this.onOK });
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	onOK = async () => {

		const { isValid, errors } = validate(this.state);
		if (!isValid) {
			this.setState({ Errors: errors });
			return;
		}

		const updatedAddress: AddressDTO = {
			City: this.state.City.trim(),
			Region: this.state.Region.trim(),
			ZipCode: this.state.ZipCode.trim(),
			Street: `${this.state.Street}, ${this.state.Number} ${this.state.FloorAndDoor}`.trim()
		};

		const location = this.state.Location || (await getAddressLocation(updatedAddress));

		if (location === null) {
			await Message.show(Res.Msg.MSG_INVALID_ADDRESS());
			return;
		}

		this.close({ updatedAddress, location });
	};

	createInitialState(_props: Props): Nullable<State> {
		return null;
	}

	onComponentDidMount(): void {

		// Confirguramos el autoComplete de Google
		this.autoComplete = new google.maps.places.Autocomplete(this.autoCompleteRef!.current!, {
			fields: ['geometry', 'address_components'],
			types: ['address'],
			componentRestrictions: { country: 'es' },
		});

		this.autoComplete.addListener('place_changed', () => {
			const { address_components, geometry } = this.autoComplete!.getPlace();
			if (!address_components || geometry === undefined)
				return;

			const location = getLocation(address_components, geometry.location);
			const deliveryAddress = this.getDeliveryAddress(address_components);

			this.setState({ ...deliveryAddress, Location: location ? { Latitude: location.lat(), Longitude: location.lng() } : null });
		});
	}

	onComponentWillUnmount(): void {
		if (this.autoComplete) {
			google.maps.event.clearInstanceListeners(this.autoComplete);
			document.querySelectorAll('.pac-container.pac-logo').forEach(el => el.remove());
		}
	}


	private getDeliveryAddress(addressComponents: google.maps.GeocoderAddressComponent[]): DeliveryAddress {
		return {
			City: getAddressComponentValue(addressComponents, 'locality').trim(),
			Region: getAddressComponentValue(addressComponents, 'administrative_area_level_2').trim(),
			Street: `${getAddressComponentValue(addressComponents, 'route')}`.trim(),
			FloorAndDoor: `${getAddressComponentValue(addressComponents, 'floor')} ${getAddressComponentValue(addressComponents, 'room')}`.trim(),
			Number: `${getAddressComponentValue(addressComponents, 'street_number')}`.trim(),
			ZipCode: getAddressComponentValue(addressComponents, 'postal_code').trim(),
		};
	}

	onInputTextChange = (property: keyof DeliveryAddress, e: React.ChangeEvent<HTMLInputElement>): void => {

		const newState = { ...this.state };
		newState[property] = e.target.value;
		// Si cambia el número, dirección o municipio, reseteo la ubicación para que se vuelva a geolacalizar
		// cuando pulsemos en el botón "Continuar"
		if (['City', 'Number', 'Street'].includes(property))
			newState['Location'] = null;

		this.setState(newState);
	};

	getContent() {

		return <>
			<UI.Cols className='m-t'>
				<UI.Col width='*'>
					<Input ref={this.autoCompleteRef} label={Res.Web.SHOW_EDIT_DELIVERY_ADDRESS_ADDRESS_LBL()} value={this.state.Street} onChange={e => this.onInputTextChange('Street', e)} error={this.state.Errors['Street']} />
				</UI.Col>
			</UI.Cols>
			<UI.Cols>
				<UI.Col width='*'>
					<Input label={Res.Web.SHOW_EDIT_DELIVERY_ADDRESS_NUMBER_LBL()} value={this.state.Number} onChange={e => this.onInputTextChange('Number', e)} error={this.state.Errors['Number']} />
				</UI.Col>
				<UI.Col width='*'>
					<Input label={Res.Web.SHOW_EDIT_DELIVERY_ADDRESS_FLOOR_LBL()} value={this.state.FloorAndDoor} onChange={e => this.onInputTextChange('FloorAndDoor', e)} error={this.state.Errors['FloorAndDoor']} />
				</UI.Col>
			</UI.Cols>
			<UI.Cols>
				<UI.Col width='*'>
					<Input label={Res.Web.SHOW_EDIT_DELIVERY_ADDRESS_CITY_LBL()} value={this.state.City} onChange={e => this.onInputTextChange('City', e)} error={this.state.Errors['City']} />
				</UI.Col>
				<UI.Col width='*'>
					<Input label={Res.Web.SHOW_EDIT_DELIVERY_ADDRESS_POSTAL_CODE_LBL()} value={this.state.ZipCode} onChange={e => this.onInputTextChange('ZipCode', e)} error={this.state.Errors['ZipCode']} />
				</UI.Col>
			</UI.Cols>
		</>;

	}
}

type EditDeliveryAdress = {
	updatedAddress: AddressDTO,
	location: AddressLocationDTO
}

export default async function tryToGetLocatedAddress(currentAddress: AddressDTO): Promise<Result<EditDeliveryAdress>> {
	return Modal.show(ShowEditDeliveryAddressScreen, { currentAddress });
}

function getAddressLocation(address: AddressDTO): Promise<AddressLocationDTO | null> {

	return new Promise(resolve => {

		const geocoder = new google.maps.Geocoder();
		const street = `${address.Street}, ${address.City} ${address.ZipCode}`;
		void geocoder.geocode({ address: street, }, (results) => {

			const locationType = results?.[0]?.geometry?.location_type || '';
			const location = results?.[0]?.geometry?.location;
			const isValidLocationType = ['ROOFTOP', 'RANGE_INTERPOLATED'].includes(locationType);

			resolve(isValidLocationType && location ? { Latitude: location.lat(), Longitude: location.lng() } : null);
		});
	});
}



function getLocation(addressComponents: google.maps.GeocoderAddressComponent[], location?: google.maps.LatLng): google.maps.LatLng | null {
	// Sólo considero la posición válida si me han introducido hasta el número. Si no, lo que
	// tengo es aproximado y tendré que geoposionar mejor más tarde, cuando tenga ya el número
	const streetNumber = getAddressComponentValue(addressComponents, 'street_number');
	return location && streetNumber ? location : null;
}

function getAddressComponentValue(address: google.maps.GeocoderAddressComponent[], type: string): string {
	const component = address.find(x => x.types.includes(type));
	if (!component)
		return '';
	return component.long_name;
}